import axios, { type AxiosInstance, type AxiosResponse } from "axios";
import { ElMessage } from "element-plus";
import router from "@/router";
import { useAuthStore } from "@/stores/auth";

// 创建 axios 实例
const apiClient: AxiosInstance = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL || "",
    timeout: 10000,
    headers: {
        "Content-Type": "application/json",
    },
});

// 请求拦截器
apiClient.interceptors.request.use(
    (config) => {
        const authStore = useAuthStore();
        if (authStore.accessToken) {
            config.headers.Authorization = `Bearer ${authStore.accessToken}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// 响应拦截器
apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
        const { data } = response;

        // 对于健康检查接口，我们不需要解包 data 字段
        if (response.config.url?.includes('/api/health')) {
            return response;
        }

        // 所有接口都返回统一格式，直接判断 code 字段
        if (data.code !== undefined && data.code !== 200) {
            ElMessage.error(data.message || "请求失败");
            return Promise.reject(new Error(data.message || "请求失败"));
        }

        // 解包 data 字段，使前端可以直接使用
        if (data.code === 200) {
            response.data = data.data;
        }

        return response;
    },
    async (error) => {
        const { response } = error;

        if (response) {
            const { status, data } = response;

            switch (status) {
                case 401:
                    // Token 过期或无效
                    const authStore = useAuthStore();
                    if (authStore.refreshToken) {
                        try {
                            // 尝试刷新 token
                            await authStore.refreshAccessToken();
                            // 重新发送请求
                            return apiClient.request(error.config);
                        } catch (refreshError) {
                            // 刷新失败，清除用户信息并跳转到登录页
                            authStore.logout();
                            router.push("/login");
                            ElMessage.error("登录已过期，请重新登录");
                            return Promise.reject(refreshError);
                        }
                    } else {
                        // 没有 refresh token，跳转到登录页
                        authStore.logout();
                        router.push("/login");
                        ElMessage.error("请先登录");
                        return Promise.reject(error);
                    }

                case 403:
                    ElMessage.error("没有权限访问该资源");
                    break;

                case 404:
                    ElMessage.error("请求的资源不存在");
                    break;

                case 500:
                    ElMessage.error("服务器内部错误");
                    break;

                default:
                    ElMessage.error(data?.message || "请求失败");
            }
        } else if (error.request) {
            // 网络错误
            ElMessage.error("网络连接失败，请检查网络设置");
        } else {
            // 其他错误
            ElMessage.error("请求配置错误");
        }

        return Promise.reject(error);
    }
);

export default apiClient;